import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { createStoredToken } from "@am/shared";
import { ApiError, setCurrentToken, useApiClient } from "../api/client";

const tokenStore = createStoredToken(
  "admin_token",
  "admin_token_expires",
  "admin_remember",
);

interface LoginResponse {
  token: string;
  expires_in: number;
  tenant_id: string;
  tenant_name: string;
  slug: string;
}

interface Session {
  token: string;
  tenantId: string;
  tenantName: string;
  slug: string;
}

interface AuthContextValue {
  status: "checking" | "anonymous" | "authenticated";
  session: Session | null;
  login: (slug: string, password: string, remember: boolean) => Promise<void>;
  logout: (opts?: { sessionExpired?: boolean }) => void;
  sessionExpiredFlash: boolean;
  consumeExpiredFlash: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const api = useApiClient();
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthContextValue["status"]>("checking");
  const [sessionExpiredFlash, setExpired] = useState(false);

  // Auto-login from stored token on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = tokenStore.read();
      if (!stored) {
        if (!cancelled) setStatus("anonymous");
        return;
      }
      setCurrentToken(stored.token);
      try {
        const me = await api<{ tenant_id: string; name: string; slug: string }>("/tenant/api/me");
        if (cancelled) return;
        setSession({
          token: stored.token,
          tenantId: me.tenant_id,
          tenantName: me.name,
          slug: me.slug,
        });
        setStatus("authenticated");
      } catch (err) {
        if (cancelled) return;
        tokenStore.clear();
        setCurrentToken(null);
        if (err instanceof ApiError && err.status === 401) setExpired(true);
        setStatus("anonymous");
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run once on mount; api is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (slug: string, password: string, remember: boolean) => {
      const res = await api<LoginResponse>("/tenant/api/login", {
        method: "POST",
        body: { slug: slug.trim().toLowerCase(), password },
      });
      tokenStore.write(res.token, res.expires_in, remember);
      setCurrentToken(res.token);
      setSession({
        token: res.token,
        tenantId: res.tenant_id,
        tenantName: res.tenant_name,
        slug: res.slug,
      });
      setStatus("authenticated");
      setExpired(false);
    },
    [api],
  );

  const logout = useCallback((opts?: { sessionExpired?: boolean }) => {
    tokenStore.clear();
    setCurrentToken(null);
    setSession(null);
    setStatus("anonymous");
    if (opts?.sessionExpired) setExpired(true);
  }, []);

  const consumeExpiredFlash = useCallback(() => setExpired(false), []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, session, login, logout, sessionExpiredFlash, consumeExpiredFlash }),
    [status, session, login, logout, sessionExpiredFlash, consumeExpiredFlash],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
